import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Helper used by other server functions to gate event-scoped permissions.
 * Returns true if the user is admin, the event organizer, or assigned staff.
 */
async function assertCanManageEventStaff(eventId: string, userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data: ev } = await supabaseAdmin
    .from("events")
    .select("organizer_id")
    .eq("id", eventId)
    .maybeSingle();
  if (!ev) throw new Response("Evento no encontrado", { status: 404 });

  if (ev.organizer_id === userId) return;

  const { data: isAdmin } = await supabaseAdmin.rpc("has_role", {
    _user_id: userId,
    _role: "admin",
  });
  if (isAdmin) return;

  throw new Response("No tenés permisos para gestionar el staff de este evento.", { status: 403 });
}

export const listEventStaff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ eventId: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await assertCanManageEventStaff(data.eventId, userId);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("event_staff")
      .select("id, event_id, user_id, role, created_at")
      .eq("event_id", data.eventId)
      .order("created_at", { ascending: false });
    if (error) throw new Response(error.message, { status: 500 });

    const userIds = (rows ?? []).map((r) => r.user_id);
    let profiles: Record<string, { display_name: string | null; email: string | null }> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabaseAdmin
        .from("profiles")
        .select("user_id, display_name")
        .in("user_id", userIds);
      for (const p of profs ?? []) {
        profiles[p.user_id] = { display_name: p.display_name, email: null };
      }
      // Fetch emails via admin API (best-effort)
      try {
        const { data: usersList } = await supabaseAdmin.auth.admin.listUsers({ page: 1, perPage: 200 });
        for (const u of usersList?.users ?? []) {
          if (userIds.includes(u.id)) {
            profiles[u.id] = { display_name: profiles[u.id]?.display_name ?? null, email: u.email ?? null };
          }
        }
      } catch {
        /* ignore */
      }
    }

    return {
      staff: (rows ?? []).map((r) => ({
        id: r.id,
        eventId: r.event_id,
        userId: r.user_id,
        role: r.role,
        createdAt: r.created_at,
        displayName: profiles[r.user_id]?.display_name ?? null,
        email: profiles[r.user_id]?.email ?? null,
      })),
    };
  });

export const addEventStaff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    z.object({
      eventId: z.string().uuid(),
      email: z.string().email().max(254),
      role: z.enum(["scanner", "manager"]).default("scanner"),
    })
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await assertCanManageEventStaff(data.eventId, userId);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Resolve user by email
    const normalizedEmail = data.email.trim().toLowerCase();
    const { data: usersList, error: lookupErr } = await supabaseAdmin.auth.admin.listUsers({
      page: 1,
      perPage: 1000,
    });
    if (lookupErr) throw new Response(`No se pudo buscar el usuario: ${lookupErr.message}`, { status: 500 });
    const target = usersList?.users?.find((u) => (u.email ?? "").toLowerCase() === normalizedEmail);
    if (!target) {
      throw new Response("No existe un usuario registrado con ese email.", { status: 404 });
    }

    // Don't add the organizer redundantly
    const { data: ev } = await supabaseAdmin
      .from("events")
      .select("organizer_id")
      .eq("id", data.eventId)
      .single();
    if (ev?.organizer_id === target.id) {
      throw new Response("Este usuario ya es el organizador del evento.", { status: 400 });
    }

    // Avoid duplicates
    const { data: existing } = await supabaseAdmin
      .from("event_staff")
      .select("id")
      .eq("event_id", data.eventId)
      .eq("user_id", target.id)
      .maybeSingle();
    if (existing) {
      throw new Response("Este usuario ya está asignado como staff del evento.", { status: 409 });
    }

    const { data: inserted, error: insErr } = await supabaseAdmin
      .from("event_staff")
      .insert({
        event_id: data.eventId,
        user_id: target.id,
        role: data.role,
        created_by: userId,
      })
      .select("id")
      .single();
    if (insErr) throw new Response(insErr.message, { status: 500 });

    return { id: inserted.id };
  });

export const updateEventStaffRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    z.object({
      staffId: z.string().uuid(),
      role: z.enum(["scanner", "manager"]),
    })
  )
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row } = await supabaseAdmin
      .from("event_staff")
      .select("event_id")
      .eq("id", data.staffId)
      .maybeSingle();
    if (!row) throw new Response("Asignación no encontrada", { status: 404 });

    await assertCanManageEventStaff(row.event_id, userId);

    const { error } = await supabaseAdmin
      .from("event_staff")
      .update({ role: data.role, updated_at: new Date().toISOString() })
      .eq("id", data.staffId);
    if (error) throw new Response(error.message, { status: 500 });

    return { success: true };
  });

export const removeEventStaff = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ staffId: z.string().uuid() }))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row } = await supabaseAdmin
      .from("event_staff")
      .select("event_id")
      .eq("id", data.staffId)
      .maybeSingle();
    if (!row) throw new Response("Asignación no encontrada", { status: 404 });

    await assertCanManageEventStaff(row.event_id, userId);

    const { error } = await supabaseAdmin
      .from("event_staff")
      .delete()
      .eq("id", data.staffId);
    if (error) throw new Response(error.message, { status: 500 });

    return { success: true };
  });

/**
 * Returns list of events where the current user can validate tickets:
 * organizer (their own events), admin (all), or assigned staff.
 * Used by /validate to show the event selector.
 */
export const listMyValidatableEvents = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: isAdmin } = await supabaseAdmin.rpc("has_role", {
      _user_id: userId,
      _role: "admin",
    });

    const eventMap = new Map<string, { id: string; title: string; date: string; role: string }>();

    if (isAdmin) {
      const { data } = await supabaseAdmin
        .from("events")
        .select("id, title, date")
        .order("date", { ascending: false })
        .limit(200);
      for (const e of data ?? []) eventMap.set(e.id, { ...e, role: "admin" });
    } else {
      // Organizer events
      const { data: ownEvents } = await supabaseAdmin
        .from("events")
        .select("id, title, date")
        .eq("organizer_id", userId);
      for (const e of ownEvents ?? []) eventMap.set(e.id, { ...e, role: "organizer" });

      // Staff events
      const { data: staffRows } = await supabaseAdmin
        .from("event_staff")
        .select("role, events:event_id(id, title, date)")
        .eq("user_id", userId);
      for (const s of staffRows ?? []) {
        const ev = (s as any).events;
        if (ev && !eventMap.has(ev.id)) eventMap.set(ev.id, { ...ev, role: s.role });
      }
    }

    return {
      events: Array.from(eventMap.values()).sort((a, b) => (a.date < b.date ? 1 : -1)),
      isAdmin: !!isAdmin,
    };
  });
